import { Grid2 } from '@mui/material'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import moment from 'moment'
import React from 'react'

import CampaignLink from '@/app/component/atom/campaign-link'
import Tooltip from '@/app/component/atom/tooltip'
import { CallProps, formatCost } from '@/app/module/logs/helpers'
import { getCallerNumber, getCallerReceiverNumber, isInbound } from '@/app/module/logs/utils'
import { dateTime } from '@/app/service/util'

import { DataCell, RowContainer, RowLoader, Truncate } from './common'
import ContactIcon from './contact-icon'
import ContactPhoneDisplay from './contact-phone-display'

const initialDate = new Date(2005, 1, 1)

const getCallStatus = ({ direction, isMissedCall, answer, finalError }: CallProps): string => {
  if (direction === 'ingress') {
    if (isMissedCall) {
      return 'Missed Call'
    }
    return 'Answered'
  }
  if (moment(answer).isAfter(initialDate)) {
    return 'Answered'
  }
  return `Not Answered: ${finalError}`
}

const checkCallTime = ({ answer, finalErrorTime, created }: CallProps): string => {
  if (moment(answer).isAfter(initialDate)) {
    return dateTime(answer)
  }
  if (moment(finalErrorTime).isAfter(initialDate)) {
    return dateTime(finalErrorTime)
  }
  return dateTime(created)
}

const processDuration = (duration: number): string => {
  if (duration === 1) {
    return '1 second'
  }
  return `${duration} seconds`
}

const CallRow: React.FC<CallProps> = (props) => {
  const {
    campaignName,
    contactDeleted,
    contactId,
    duration,
    engagementFlowId,
    isLoadingRow,
    isMissedCall,
    viewContact,
  } = props

  const isInboundType = isInbound(props)
  const contactNumber = getCallerNumber(props)
  const messageStatus = getCallStatus(props)

  const callTime = checkCallTime(props)

  return (
    <RowContainer>
      {isLoadingRow ? (
        <CardContent>
          <Grid2 container spacing={8}>
            <RowLoader size={{ xs: 12 }}>
              <CircularProgress color="primary" size={50} />
            </RowLoader>
          </Grid2>
        </CardContent>
      ) : (
        <CardContent>
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 3, md: 3 }}>
              <ContactPhoneDisplay phoneNumber={contactNumber} direction={isInboundType ? 'inbound' : 'outbound'} />
            </Grid2>
            <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <span>{messageStatus}</span>
              </Typography>
              <Typography color="primary" align="center" variant="body1">
                <b>({formatCost(props.chargedMicro)})</b>
              </Typography>
            </DataCell>
            <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <Tooltip title={'Date and Time'}>
                  <span>&nbsp;{callTime}</span>
                </Tooltip>
              </Typography>
            </DataCell>
            <DataCell size={{ xs: 2, sm: 2, md: 2 }}>
              <CampaignLink align="center" campaignId={engagementFlowId} campaignName={campaignName} />
            </DataCell>
            <DataCell size={{ xs: 2, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <Tooltip title={'From/To'}>
                  <span>{getCallerReceiverNumber(props)}</span>
                </Tooltip>
              </Typography>
            </DataCell>
            <Grid2 size={{ xs: 2, sm: 1, md: 1 }}>
              <ContactIcon contactDeleted={contactDeleted} contactId={contactId} onClick={viewContact} />
            </Grid2>
          </Grid2>
          <Grid2 container>
            <Grid2 size={{ xs: 12 }}>
              <Truncate variant="subtitle1" color="textPrimary">
                {isMissedCall ? '0 seconds' : processDuration(duration)}
              </Truncate>
            </Grid2>
          </Grid2>
        </CardContent>
      )}
    </RowContainer>
  )
}

export default CallRow
